// src/pages/CompanyPages/ManageCompanies.js

import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Drawer,
  TextField,
  Card,
  CardContent,
} from "@mui/material";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { useNavigate, useParams } from "react-router-dom";
import Swal from "sweetalert2";

import axiosInstance from "../../Services/axiosConfig";
import Sidebar from "../../components/CompanySidebar";
import Footer from "../../components/Footer";
import FeedbackButton from "../../components/FeedbackButton";
import FeedbackSnackbar from "../../components/FeedbackSnackbar";
import { useThemeMode } from "../../components/ThemeModeContext";

export default function ManageCompanies() {
  const { companyId } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem("authToken");

  // Estado para lista de empresas
  const [companies, setCompanies] = useState([]);
  // Estado de carregamento
  const [loading, setLoading] = useState(false);

  // Drawer para criar nova empresa
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Formulário de criação
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    address: "",
    zip_code: "",
    city: "",
    cnpj: "",
    phone: "",
  });

  // Snackbar (feedback)
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMsg, setSnackbarMsg] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  // Dark/Light Mode
  const { mode } = useThemeMode();
  const theme = useMemo(
    () =>
      createTheme({
        palette: {
          mode,
          background: {
            default: mode === "light" ? "#FAF5E4" : "#121212",
            paper: mode === "light" ? "#FFFFFF" : "#1E1E1E",
          },
          text: {
            primary: mode === "light" ? "#1A202C" : "#FFFFFF",
          },
        },
      }),
    [mode]
  );

  // Fecha o Snackbar
  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };

  // Busca lista de empresas
  const fetchCompanies = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get(`companies/${token}/${companyId}/`);
      if (response.data.status === "success") {
        const companyList = Object.entries(response.data.data).map(([id, company]) => ({
          id,
          ...company,
        }));
        setCompanies(companyList);
      }
    } catch (error) {
      console.error("Error fetching companies", error);
      setOpenSnackbar(true);
      setSnackbarMsg("Error fetching companies");
      setSnackbarSeverity("error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCompanies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  // Handle form changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Cria nova empresa
  const handleSubmit = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.post(`new-company/${token}/${companyId}/`, formData);
      if (response.data.status === "success") {
        Swal.fire("Success", "Company created successfully", "success");
        setDrawerOpen(false);
        fetchCompanies();
        setFormData({
          name: "",
          description: "",
          address: "",
          zip_code: "",
          city: "",
          cnpj: "",
          phone: "",
        });
      } else {
        Swal.fire("Error", response.data.message, "error");
      }
    } catch (error) {
      console.error("Error creating company", error);
      Swal.fire("Error", "An error occurred. Please try again.", "error");
    } finally {
      setLoading(false);
    }
  };

  // Navega para o dashboard da empresa ao clicar no card
  const handleNavigate = (id) => {
    navigate(`/company-dashboard/${id}`);
  };

  return (
    <ThemeProvider theme={theme}>
      {/* Layout vertical */}
      <Box display="flex" flexDirection="column" minHeight="100vh" bgcolor="background.default">
        
        {/* Layout horizontal: Sidebar + Conteúdo */}
        <Box display="flex" flexGrow={1}>
          <Sidebar companyId={companyId} />

          <Box flex={1} p={3} color="text.primary">
            <Typography variant="h4" gutterBottom>
              Manage Companies
            </Typography>

            <Button
              variant="contained"
              color="primary"
              onClick={() => setDrawerOpen(true)}
              sx={{ mb: 3 }}
            >
              Create Company
            </Button>

            {loading ? (
              <Box display="flex" justifyContent="center" alignItems="center" mt={3}>
                <CircularProgress />
              </Box>
            ) : (
              <Box>
                {companies.map((company) => (
                  <Card
                    key={company.id}
                    sx={{ mb: 2, cursor: "pointer" }}
                    onClick={() => handleNavigate(company.id)} // Redirecionar ao clicar no card
                  >
                    <CardContent>
                      <Typography variant="h6">{company.name}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {company.description}
                      </Typography>
                      <Typography variant="body2">
                        Address: {company.address}
                      </Typography>
                      <Typography variant="body2">
                        Zip Code: {company.zip_code}
                      </Typography>
                      <Typography variant="body2">City: {company.city}</Typography>
                      <Typography variant="body2">CNPJ: {company.cnpj}</Typography>
                      <Typography variant="body2">Phone: {company.phone}</Typography>
                      <Typography variant="body2">
                        Verified: {company.verified ? "Yes" : "No"}
                      </Typography>
                    </CardContent>
                  </Card>
                ))}
              </Box>
            )}
          </Box>
        </Box>

        {/* Botão de Feedback (Darkmode + feedback) */}
        <FeedbackButton />

        {/* Footer */}
        <Footer />

        {/* Drawer para criação de nova empresa */}
        <Drawer
          anchor="right"
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
        >
          <Box width={400} p={3} bgcolor="background.default" color="text.primary">
            <Typography variant="h6" gutterBottom>
              Create Company
            </Typography>

            <TextField
              label="Name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              fullWidth
              required
              margin="normal"
            />

            <TextField
              label="Description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              multiline
              rows={4}
              fullWidth
              required
              margin="normal"
            />

            <TextField
              label="Address"
              name="address"
              value={formData.address}
              onChange={handleChange}
              fullWidth
              required
              margin="normal"
            />

            <TextField
              label="Zip Code"
              name="zip_code"
              value={formData.zip_code}
              onChange={handleChange}
              fullWidth
              required
              margin="normal"
            />

            <TextField
              label="City"
              name="city"
              value={formData.city}
              onChange={handleChange}
              fullWidth
              required
              margin="normal"
            />

            <TextField
              label="CNPJ"
              name="cnpj"
              value={formData.cnpj}
              onChange={handleChange}
              fullWidth
              required
              margin="normal"
            />

            <TextField
              label="Phone"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              fullWidth
              required
              margin="normal"
            />

            <Button
              variant="contained"
              color="primary"
              onClick={handleSubmit}
              disabled={loading}
              fullWidth
              sx={{ mt: 2 }}
            >
              {loading ? <CircularProgress size={20} color="inherit" /> : "Save"}
            </Button>
          </Box>
        </Drawer>

        {/* Snackbar para feedback */}
        <FeedbackSnackbar
          open={openSnackbar}
          handleClose={() => setOpenSnackbar(false)}
          message={snackbarMsg}
          severity={snackbarSeverity}
        />
      </Box>
    </ThemeProvider>
  );
}
